// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface RosterData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  team_id: string;
  profile_id: string | null;
  magic_link_token: string | null;
  magic_link_expires_at: string | null;
  coach_teams: {
    id: string;
    name: string;
  } | null;
}

const JoinTeamComplete: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [rosterData, setRosterData] = useState<RosterData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Form state for new user creation
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const token = searchParams.get('token'); // roster_id
  const magicToken = searchParams.get('magic');

  useEffect(() => {
    if (!token || !magicToken) {
      setError('Invalid link. Missing required parameters.');
      setLoading(false);
      return;
    }

    verifyMagicLink();
  }, [token, magicToken]);

  const verifyMagicLink = async () => {
    try {
      setLoading(true);
      console.log('🔍 [JOIN-TEAM-COMPLETE] Verifying magic link:', { token, magicToken: magicToken?.substring(0, 10) + '...' });

      // Fetch roster with magic token verification
      const { data: roster, error: rosterError } = await supabase
        .from('coach_team_rosters')
        .select(`
          id, first_name, last_name, email, team_id, profile_id,
          magic_link_token, magic_link_expires_at,
          coach_teams:team_id (id, name)
        `)
        .eq('id', token)
        .single();

      if (rosterError || !roster) {
        console.error('❌ [JOIN-TEAM-COMPLETE] Roster fetch error:', rosterError);
        setError('Invalid invitation link.');
        setLoading(false);
        return;
      }

      // Verify magic token matches
      if (roster.magic_link_token !== magicToken) {
        console.error('❌ [JOIN-TEAM-COMPLETE] Token mismatch');
        setError('Invalid or expired magic link. Please request a new one.');
        setLoading(false);
        return;
      }

      // Check expiration
      if (roster.magic_link_expires_at) {
        const expiresAt = new Date(roster.magic_link_expires_at);
        if (expiresAt < new Date()) {
          console.error('❌ [JOIN-TEAM-COMPLETE] Token expired');
          setError('This magic link has expired. Please request a new one.');
          setLoading(false);
          return;
        }
      }

      // Check if already linked to a profile
      if (roster.profile_id) {
        console.log('✅ [JOIN-TEAM-COMPLETE] Already linked to profile');
        setSuccess(true);
        setLoading(false);
        return;
      }

      console.log('✅ [JOIN-TEAM-COMPLETE] Magic link verified:', roster);
      
      // Handle coach_teams join result (could be array from join)
      const teamData = Array.isArray(roster.coach_teams) 
        ? roster.coach_teams[0] 
        : roster.coach_teams;
      
      // Check if user already has an account
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id, user_id, email')
        .eq('email', roster.email)
        .maybeSingle();

      if (existingProfile?.user_id) {
        console.log('🔄 [JOIN-TEAM-COMPLETE] Existing user detected, linking profile to roster');
        
        // Link their existing profile to the roster
        const { error: updateError } = await supabase
          .from('coach_team_rosters')
          .update({
            profile_id: existingProfile.id,
            magic_link_token: null,
            magic_link_expires_at: null,
          })
          .eq('id', roster.id);

        if (updateError) {
          console.error('❌ [JOIN-TEAM-COMPLETE] Failed to link existing profile:', updateError);
          setError('Failed to link your account to the team. Please try logging in.');
          setLoading(false);
          return;
        }

        console.log('✅ [JOIN-TEAM-COMPLETE] Existing user linked successfully');
        toast.success(`You've been added to ${teamData?.name || 'the team'}!`);
        setSuccess(true);
        setLoading(false);
        return;
      }
      
      setRosterData({
        ...roster,
        coach_teams: teamData,
      } as RosterData);
      setLoading(false);
    } catch (err) {
      console.error('❌ [JOIN-TEAM-COMPLETE] Exception:', err);
      setError('An unexpected error occurred.');
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    if (!rosterData) return;

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setProcessing(true);
      console.log('🔐 [JOIN-TEAM-COMPLETE] Creating account for:', rosterData.email);

      // Sign up the user with their email and password
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: rosterData.email,
        password,
        options: {
          data: {
            first_name: rosterData.first_name,
            last_name: rosterData.last_name,
            full_name: `${rosterData.first_name} ${rosterData.last_name}`.trim(),
            role: 'athlete',
            roster_id: rosterData.id,
          },
        },
      });

      if (signUpError) {
        console.error('❌ [JOIN-TEAM-COMPLETE] Sign up error:', signUpError);

        // If user already exists, try to sign in instead
        if (signUpError.message.includes('already registered')) {
          toast.error('This email already has an account. Please log in instead.');
          navigate(`/auth/login?redirect=${encodeURIComponent(`/join-team?token=${token}`)}`);
          return;
        }

        throw signUpError;
      }

      console.log('✅ [JOIN-TEAM-COMPLETE] User created:', signUpData.user?.id);

      // IMPORTANT: Ensure an authenticated session exists before sending user to protected routes.
      // Depending on Supabase auth settings, signUp may not create a session.
      if (!signUpData.session) {
        console.log('🔑 [JOIN-TEAM-COMPLETE] No session returned from signUp; signing in with password');
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email: rosterData.email,
          password,
        });

        if (signInError) {
          console.error('❌ [JOIN-TEAM-COMPLETE] Auto sign-in failed:', signInError);
          toast.error('Account created, but we could not sign you in automatically. Please log in.');
          navigate('/auth/login', { replace: true });
          return;
        }

        console.log('✅ [JOIN-TEAM-COMPLETE] Auto sign-in success:', signInData.user?.id);
      }

      // Link the profile to the roster (best-effort)
      if (signUpData.user) {
        const { error: updateError } = await supabase
          .from('coach_team_rosters')
          .update({
            profile_id: signUpData.user.id,
            magic_link_token: null, // Clear the magic token
            magic_link_expires_at: null,
          })
          .eq('id', rosterData.id);

        if (updateError) {
          console.error('❌ [JOIN-TEAM-COMPLETE] Profile link error:', updateError);
          // Don't throw - the account was created, just the linking failed
          toast.warning('Account created but there was an issue linking to your team. Please contact your coach.');
        } else {
          console.log('✅ [JOIN-TEAM-COMPLETE] Profile linked to roster');
        }
      }

      setSuccess(true);
      toast.success(`Welcome to ${rosterData.coach_teams?.name}!`);
    } catch (err: any) {
      console.error('❌ [JOIN-TEAM-COMPLETE] Exception:', err);
      toast.error(err.message || 'Failed to create account');
    } finally {
      setProcessing(false);
    }
  };

  const teamName = rosterData?.coach_teams?.name || 'your team';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Verifying your link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-6 w-6" />
              <CardTitle>Link Error</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Button onClick={() => navigate('/')} className="w-full mt-6">
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2 text-success">
              <CheckCircle2 className="h-6 w-6" />
              <CardTitle>Welcome to the Team!</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-success text-success">
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                Your account has been created and you've been added to <strong>{teamName}</strong>!
              </AlertDescription>
            </Alert>
            <p className="text-muted-foreground text-sm">
              You can now access your athlete dashboard to view team information and complete your profile.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => { window.location.href = '/athlete-dashboard'; }} className="w-full">
                Go to Dashboard
              </Button>
              <Button onClick={() => navigate('/')} variant="outline" className="w-full">
                Go to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show account creation form
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Create Your Account</CardTitle>
          <CardDescription>
            Join <strong>{teamName}</strong> on RecruitLook
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-muted p-4 rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">Creating account for:</p>
            <p className="font-medium">
              {rosterData?.first_name} {rosterData?.last_name}
            </p>
            <p className="text-sm text-muted-foreground">{rosterData?.email}</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Choose a password"
                disabled={processing}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                disabled={processing}
              />
            </div>

            <Button
              onClick={handleCreateAccount}
              className="w-full"
              size="lg"
              disabled={processing || !password || !confirmPassword}
            >
              {processing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Create Account & Join Team'
              )}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            By creating an account, you agree to our Terms of Service and Privacy Policy.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default JoinTeamComplete;